// SCR-STAFF-507 (US-CLN-055؛ BR-CLN-054): ثبت عارضه از جلسه یا پرونده، پیگیری با موعد (عارضه شدید: موعد پیش‌فرض ۲۴ ساعت و اعلان Manager و مسئول
// فنی سمت سرور) و گزارش عوارض به تفکیک دستگاه و بچ محصول (بدون شناسه مراجعه‌کننده — C-AGENT-892).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ClnAdverseEventSeverity,
  type ClnAdverseEventStatus,
  type ClnAdverseEventType,
  type ClnAdverseEventView,
  type ClnAdverseReportGroup,
  type ClnAdverseReportRow,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, ListPage, LoadingState, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ADVERSE_OUTCOMES, ADVERSE_SEVERITIES, ADVERSE_TYPES, beautyKeys, SCR_STAFF_507 } from './beautyCommon';
import { clnKeys, num } from './common';
import { todayIn } from '../organization/deviceCommon';

const SEVERITY_COLORS: Record<ClnAdverseEventSeverity, 'default' | 'warning' | 'error'> = { Mild: 'default', Moderate: 'warning', Severe: 'error' };

export function SeverityChip({ severity }: { severity: ClnAdverseEventSeverity }) {
  const { t } = useTranslation('cln');
  return <Chip size="small" color={SEVERITY_COLORS[severity]} label={t(`cln:adverse_severity.${severity}`)} />;
}

export interface AdverseEventDialogProps {
  patientId: string;
  encounterId?: string | null;
  sessionId?: string | null;
  onClose: () => void;
  onSaved?: (view: ClnAdverseEventView) => void;
}

/** ثبت عارضه (AC-CLN-055.1)؛ دستگاه و بچ از جلسه در سرور پیش‌فرض می‌شوند. */
export function AdverseEventDialog({ patientId, encounterId, sessionId, onClose, onSaved }: AdverseEventDialogProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [type, setType] = useState<ClnAdverseEventType>('Burn');
  const [severity, setSeverity] = useState<ClnAdverseEventSeverity>('Mild');
  const [onsetDate, setOnsetDate] = useState(todayIn(timeZone));
  const [description, setDescription] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/adverse-events', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': key.current() },
          body: {
            type,
            severity,
            onsetDate,
            sessionId: sessionId ?? null,
            encounterId: encounterId ?? null,
            deviceId: null,
            itemId: null,
            batchId: null,
            description: description.trim() || null,
            actionTaken: actionTaken.trim() || null,
            followUpDueAt: null,
          },
        }),
      ),
    onSettled: (_data, error) => key.settle(error),
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: beautyKeys.adverse(session?.tenantId, patientId) });
      void queryClient.invalidateQueries({ queryKey: clnKeys.pending(session?.tenantId) });
      onSaved?.(view);
      onClose();
    },
  });
  const descriptionMissing = type === 'Other' && !description.trim();
  const submit = () => {
    setTouched(true);
    if (!descriptionMissing && onsetDate) {
      save.mutate();
    }
  };
  return (
    <FormDialog open titleKey="cln:adverse.report" screenId={SCR_STAFF_507} onSubmit={submit} onClose={onClose} isDirty={Boolean(description || actionTaken)} isSubmitting={save.isPending} submitError={save.error}>
      <TextField select fullWidth required label={t('cln:adverse.type')} value={type} onChange={(event) => setType(event.target.value as ClnAdverseEventType)} slotProps={{ htmlInput: { 'data-testid': 'cln-adverse-type' } }}>
        {ADVERSE_TYPES.map((option) => (
          <MenuItem key={option} value={option}>
            {t(`cln:adverse_type.${option}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField select fullWidth required label={t('cln:adverse.severity')} value={severity} onChange={(event) => setSeverity(event.target.value as ClnAdverseEventSeverity)} slotProps={{ htmlInput: { 'data-testid': 'cln-adverse-severity' } }}>
        {ADVERSE_SEVERITIES.map((option) => (
          <MenuItem key={option} value={option}>
            {t(`cln:adverse_severity.${option}`)}
          </MenuItem>
        ))}
      </TextField>
      {severity === 'Severe' && <Alert severity="warning">{t('cln:adverse.severe_hint')}</Alert>}
      <TextField type="date" fullWidth required label={t('cln:adverse.onset')} value={onsetDate} onChange={(event) => setOnsetDate(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIn(timeZone), 'data-testid': 'cln-adverse-onset' } }} />
      <TextField
        fullWidth
        multiline
        minRows={2}
        required={type === 'Other'}
        error={touched && descriptionMissing}
        label={t('cln:adverse.description')}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'cln-adverse-description' } }}
      />
      <TextField fullWidth multiline minRows={2} label={t('cln:adverse.action_taken')} value={actionTaken} onChange={(event) => setActionTaken(event.target.value)} slotProps={{ htmlInput: { maxLength: 2000 } }} />
    </FormDialog>
  );
}

/** ثبت پیگیری (AC-CLN-055.2): پیامد و موعد بعدی. */
export function FollowUpDialog({ event, onClose }: { event: ClnAdverseEventView; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<ClnAdverseEventStatus>('UnderFollowUp');
  const [nextDue, setNextDue] = useState('');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/adverse-events/{id}/follow-ups', {
          params: { path: { id: event.id } },
          headers: { 'Idempotency-Key': key.current() },
          body: { note: note.trim(), outcome, nextDueAt: nextDue ? new Date(nextDue).toISOString() : null },
        }),
      ),
    onSettled: (_data, error) => key.settle(error),
    onSuccess: (view) => {
      queryClient.setQueryData(beautyKeys.adverseOne(session?.tenantId, event.id), view);
      void queryClient.invalidateQueries({ queryKey: beautyKeys.adverse(session?.tenantId, event.patientId) });
      void queryClient.invalidateQueries({ queryKey: clnKeys.pending(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="cln:adverse.follow_up" onSubmit={() => note.trim() && save.mutate()} onClose={onClose} isDirty={Boolean(note)} isSubmitting={save.isPending} submitError={save.error}>
      <TextField fullWidth multiline minRows={3} required label={t('cln:adverse.follow_up_note')} value={note} onChange={(item) => setNote(item.target.value)} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'cln-adverse-followup-note' } }} />
      <TextField select fullWidth label={t('cln:adverse.outcome')} value={outcome} onChange={(item) => setOutcome(item.target.value as ClnAdverseEventStatus)} slotProps={{ htmlInput: { 'data-testid': 'cln-adverse-outcome' } }}>
        {ADVERSE_OUTCOMES.map((option) => (
          <MenuItem key={option} value={option}>
            {t(`cln:adverse_status.${option}`)}
          </MenuItem>
        ))}
      </TextField>
      {outcome === 'UnderFollowUp' && (
        <TextField type="datetime-local" fullWidth label={t('cln:adverse.next_due')} value={nextDue} onChange={(item) => setNextDue(item.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      )}
    </FormDialog>
  );
}

/** جزئیات عارضه با پیگیری‌ها. */
export function AdverseEventDetail({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const [following, setFollowing] = useState(false);
  const detail = useQuery({
    queryKey: beautyKeys.adverseOne(session?.tenantId, eventId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/adverse-events/{id}', { params: { path: { id: eventId } } })),
  });
  const data = detail.data;
  const open = data && (data.status === 'Reported' || data.status === 'UnderFollowUp');
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-screen-id={SCR_STAFF_507}>
      <DialogTitle>{t('cln:adverse.detail')}</DialogTitle>
      <DialogContent>
        {detail.isPending && <LoadingState rows={3} />}
        {detail.error ? <ErrorState error={detail.error} compact /> : null}
        {data && (
          <Stack spacing={1} data-testid="cln-adverse-detail">
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1">{t(`cln:adverse_type.${data.type}`)}</Typography>
              <SeverityChip severity={data.severity} />
              <Chip size="small" variant="outlined" label={t(`cln:adverse_status.${data.status}`)} data-testid="cln-adverse-status" />
              {data.overdue && <Chip size="small" color="error" label={t('cln:adverse.overdue')} />}
            </Stack>
            <Typography variant="body2">
              {t('cln:adverse.onset')}: {toJalali(data.onsetDate)}
              {data.followUpDueAt ? ` · ${t('cln:adverse.due')}: ${toJalaliDateTime(data.followUpDueAt, { timeZone })}` : ''}
            </Typography>
            {(data.deviceName || data.itemName) && (
              <Typography variant="body2" color="text.secondary">
                {[data.deviceName, data.itemName, data.batchNumber].filter(Boolean).join(' · ')}
              </Typography>
            )}
            {data.description && <Typography variant="body2">{data.description}</Typography>}
            {data.actionTaken && (
              <Typography variant="body2">
                {t('cln:adverse.action_taken')}: {data.actionTaken}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              {t('cln:adverse.follow_ups')}
            </Typography>
            {data.followUps.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
            {data.followUps.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 1 }}>
                <Typography variant="body2">
                  {toJalaliDateTime(item.recordedAt, { timeZone })} · {item.recordedByName ?? ''} · {t(`cln:adverse_status.${item.outcome}`)}
                </Typography>
                <Typography variant="body2">{item.note}</Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {open && access.can('cln.adverse_event.report') && (
          <Button onClick={() => setFollowing(true)} data-testid="cln-adverse-followup">
            {t('cln:adverse.follow_up')}
          </Button>
        )}
        <Button onClick={onClose}>{t('cln:common.close')}</Button>
      </DialogActions>
      {following && data && <FollowUpDialog event={data} onClose={() => setFollowing(false)} />}
    </Dialog>
  );
}

/** عوارض یک مراجعه‌کننده (برگه پرونده). */
export function AdverseEventsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const list = useQuery({
    queryKey: beautyKeys.adverse(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/adverse-events', { params: { path: { patientId } } })),
  });
  const canReport = access.accessMode === 'Full' && access.can('cln.adverse_event.report');
  const columns: DataTableColumn<ClnAdverseEventView>[] = [
    { id: 'onset', headerKey: 'cln:adverse.onset', cell: (row) => toJalali(row.onsetDate) },
    { id: 'type', headerKey: 'cln:adverse.type', cell: (row) => t(`cln:adverse_type.${row.type}`) },
    { id: 'severity', headerKey: 'cln:adverse.severity', cell: (row) => <SeverityChip severity={row.severity} /> },
    {
      id: 'status',
      headerKey: 'cln:adverse.status',
      cell: (row) => (
        <>
          {t(`cln:adverse_status.${row.status}`)} {row.overdue ? <Chip size="small" color="error" label={t('cln:adverse.overdue')} /> : null}
        </>
      ),
    },
    { id: 'source', headerKey: 'cln:adverse.source', cell: (row) => [row.deviceName, row.itemName, row.batchNumber].filter(Boolean).join(' · ') || '—' },
  ];
  return (
    <Stack spacing={1} data-testid="cln-adverse-panel" data-screen-id={SCR_STAFF_507}>
      {canReport && (
        <Stack direction="row">
          <Button variant="outlined" onClick={() => setCreating(true)} data-testid="cln-adverse-add">
            {t('cln:adverse.report')}
          </Button>
        </Stack>
      )}
      <DataTable
        columns={columns}
        rows={list.data?.items}
        getRowId={(row) => row.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => setSelected(row.id)}
        captionKey="cln:adverse.title"
        emptyTitleKey="cln:adverse.empty"
      />
      {creating && <AdverseEventDialog patientId={patientId} onClose={() => setCreating(false)} />}
      {selected && <AdverseEventDetail eventId={selected} onClose={() => setSelected(null)} />}
    </Stack>
  );
}

/** گزارش عوارض (AC-CLN-055.3) به تفکیک دستگاه و بچ؛ پیوند اعلان «?id=» جزئیات را باز می‌کند. */
export function AdverseEventsReportPage() {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const [params, setParams] = useSearchParams();
  const [from, setFrom] = useState(todayIn(timeZone, -90));
  const [to, setTo] = useState(todayIn(timeZone));
  const selected = params.get('id');
  const report = useQuery({
    queryKey: beautyKeys.adverseReport(session?.tenantId, `${from}|${to}`),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/reports/adverse-events', { params: { query: { from, to } } })),
    enabled: Boolean(from && to),
  });
  const groupColumns = (headerKey: string): DataTableColumn<ClnAdverseReportGroup>[] => [
    { id: 'name', headerKey, cell: (row) => row.name ?? row.id.slice(0, 8) },
    { id: 'count', headerKey: 'cln:adverse.count', cell: (row) => num(row.count) },
    { id: 'severe', headerKey: 'cln:adverse.severe_count', cell: (row) => num(row.severe) },
  ];
  const rowColumns: DataTableColumn<ClnAdverseReportRow>[] = [
    { id: 'onset', headerKey: 'cln:adverse.onset', cell: (row) => toJalali(row.onsetDate) },
    { id: 'type', headerKey: 'cln:adverse.type', cell: (row) => t(`cln:adverse_type.${row.type}`) },
    { id: 'severity', headerKey: 'cln:adverse.severity', cell: (row) => <SeverityChip severity={row.severity} /> },
    { id: 'status', headerKey: 'cln:adverse.status', cell: (row) => t(`cln:adverse_status.${row.status}`) },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_507}
      titleKey="cln:adverse.report_title"
      subtitleKey="cln:adverse.report_subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField type="date" size="small" label={t('cln:adverse.from')} value={from} onChange={(event) => setFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField type="date" size="small" label={t('cln:adverse.to')} value={to} onChange={(event) => setTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        </Stack>
      }
    >
      <Grid container spacing={2} data-testid="cln-adverse-report">
        <Grid size={{ xs: 12, md: 6 }}>
          <DataTable columns={groupColumns('cln:adverse.device')} rows={report.data?.byDevice} getRowId={(row) => row.id} isLoading={report.isPending} error={report.error} captionKey="cln:adverse.by_device" emptyTitleKey="cln:adverse.empty" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DataTable columns={groupColumns('cln:adverse.batch')} rows={report.data?.byBatch} getRowId={(row) => row.id} isLoading={report.isPending} error={report.error} captionKey="cln:adverse.by_batch" emptyTitleKey="cln:adverse.empty" />
        </Grid>
        <Grid size={12}>
          <DataTable
            columns={rowColumns}
            rows={report.data?.items}
            getRowId={(row) => row.id}
            isLoading={report.isPending}
            error={report.error}
            onRowClick={(row) => setParams({ id: row.id })}
            captionKey="cln:adverse.title"
            emptyTitleKey="cln:adverse.empty"
          />
        </Grid>
      </Grid>
      {selected && <AdverseEventDetail eventId={selected} onClose={() => setParams({})} />}
    </ListPage>
  );
}
